import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { PageHeader } from '../../components/molecules';

interface MedicalRecord {
  id: string;
  patientName: string;
  ownerName: string;
  date: string;
  veterinarian: string;
  type: string;
  diagnosis: string;
  treatment: string;
  medications: string[];
  followUpDate: string;
  notes: string;
  cost: number;
}

const recordTypes = [
  'Regular Checkup',
  'Emergency Visit',
  'Surgery',
  'Vaccination',
  'Dental Care',
  'Diagnostic Testing',
  'Specialist Consultation',
  'Follow-up Visit'
];

const veterinarianOptions = [
  'Dr. Sarah Johnson',
  'Dr. Michael Chen',
  'Dr. Emily Rodriguez',
  'Dr. James Wilson'
];

const MedicalRecords: React.FC = () => {
  const [records, setRecords] = useState<MedicalRecord[]>([
    {
      id: '1',
      patientName: 'Max',
      ownerName: 'John Doe',
      date: '2024-01-10',
      veterinarian: 'Dr. Sarah Johnson',
      type: 'Regular Checkup',
      diagnosis: 'Healthy, minor dental tartar',
      treatment: 'Dental cleaning recommended',
      medications: ['Heartgard Plus', 'Frontline Plus'],
      followUpDate: '2024-04-10',
      notes: 'Patient is active and healthy. Weight is stable.',
      cost: 150
    },
    {
      id: '2',
      patientName: 'Luna',
      ownerName: 'Jane Smith',
      date: '2024-01-08',
      veterinarian: 'Dr. Michael Chen',
      type: 'Emergency Visit',
      diagnosis: 'Gastrointestinal upset',
      treatment: 'IV fluids, anti-nausea medication',
      medications: ['Metoclopramide', 'Probiotics'],
      followUpDate: '2024-01-15',
      notes: 'Responding well to treatment. Dietary changes recommended.',
      cost: 450
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState('');

  const [formData, setFormData] = useState({
    patientName: '',
    ownerName: '',
    date: '',
    veterinarian: '',
    type: 'Regular Checkup',
    diagnosis: '',
    treatment: '',
    medications: '',
    followUpDate: '',
    notes: '',
    cost: ''
  });

  const patientOptions = useMemo(() => Array.from(new Set(records.map((record) => record.patientName))), [records]);

  const filteredRecords = useMemo(
    () =>
      records.filter((record) => {
        const matchesSearch =
          record.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.diagnosis.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPatient = !selectedPatient || record.patientName === selectedPatient;
        return matchesSearch && matchesPatient;
      }),
    [records, searchTerm, selectedPatient]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newRecord: MedicalRecord = {
        id: Date.now().toString(),
        ...formData,
        medications: formData.medications
          .split(',')
          .map((med) => med.trim())
          .filter(Boolean),
        cost: parseFloat(formData.cost) || 0
      };
      setRecords((prev) => [...prev, newRecord]);
      setFormData({
        patientName: '',
        ownerName: '',
        date: '',
        veterinarian: '',
        type: 'Regular Checkup',
        diagnosis: '',
        treatment: '',
        medications: '',
        followUpDate: '',
        notes: '',
        cost: ''
      });
      setShowForm(false);
      toast.success(`Medical record for ${newRecord.patientName} has been added.`);
    } catch (err) {
      toast.error('Failed to add medical record. Please try again.');
    }
  };

  const handleDelete = (id: string) => {
    try {
      const record = records.find((r) => r.id === id);
      setRecords((prev) => prev.filter((record) => record.id !== id));
      if (record) {
        toast.success(`Medical record for ${record.patientName} was removed.`);
      }
    } catch (err) {
      toast.error('Failed to delete medical record. Please try again.');
    }
  };

  const followUpSoon = records.filter((record) => {
    if (!record.followUpDate) return false;
    const followUp = new Date(record.followUpDate);
    const today = new Date();
    const diff = (followUp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 14;
  }).length;
  const uniqueVets = Array.from(new Set(records.map((record) => record.veterinarian))).length;
  const filtersActive = Boolean(searchTerm) || Boolean(selectedPatient);
  const filterSummary = filtersActive
    ? [
        searchTerm ? `Keyword: "${searchTerm}"` : null,
        selectedPatient ? `Patient: ${selectedPatient}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : 'All records in view';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Care history"
        title="Medical records"
        description="Track diagnoses, treatments, and follow-up plans from every visit."
        icon="🩺"
        accent="rose"
        actions={(
          <button
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
            onClick={() => setShowForm(true)}
          >
            <span className="text-lg leading-none">✚</span>
            Add medical record
          </button>
        )}
        stats={[
          {
            label: 'Records',
            value: records.length,
            helper: 'Stored visits'
          },
          {
            label: 'Follow-ups soon',
            value: followUpSoon,
            helper: 'Next 14 days'
          },
          {
            label: 'Active vets',
            value: uniqueVets,
            helper: 'Contributors'
          }
        ]}
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <label className="flex w-full flex-col gap-2 text-sm font-semibold text-slate-600">
            Search records
            <input
              type="text"
              placeholder="Search by patient, owner, or diagnosis…"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
          <label className="flex w-full flex-col gap-2 text-sm font-semibold text-slate-600">
            Filter by patient
            <select
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
            >
              <option value="">All patients</option>
              {patientOptions.map((patient) => (
                <option key={patient} value={patient}>
                  {patient}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="mt-3 text-xs text-slate-500">{filterSummary}</p>
      </PageHeader>

      <section className="space-y-4">
        {filteredRecords.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-brand-muted bg-white/70 px-8 py-16 text-center shadow-soft">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 text-3xl text-brand-primary">
              📋
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-700">
              No medical records found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Adjust your filters or add a new record to capture the patient’s medical history.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredRecords.map((record) => (
              <article
                key={record.id}
                className="flex h-full flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-xl"
              >
                <header className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-secondary/10 text-xl text-brand-secondary">
                      {record.patientName.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{record.patientName}</h3>
                      <p className="text-sm text-slate-500">Owner: {record.ownerName}</p>
                      <span className="mt-2 inline-flex rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-semibold text-brand-primary">
                        {record.type}
                      </span>
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <p className="font-semibold text-slate-700">
                      {new Date(record.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <p>{record.veterinarian}</p>
                  </div>
                </header>

                <dl className="grid gap-4 text-sm">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                    <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      Diagnosis
                    </dt>
                    <dd className="mt-2 text-slate-700">{record.diagnosis}</dd>
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                    <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      Treatment plan
                    </dt>
                    <dd className="mt-2 text-slate-700">{record.treatment}</dd>
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                    <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      Medications
                    </dt>
                    <dd className="mt-2 flex flex-wrap gap-2">
                      {record.medications.length > 0 ? (
                        record.medications.map((med, index) => (
                          <span
                            key={`${record.id}-med-${index}`}
                            className="rounded-full border border-brand-accent/30 bg-brand-accent/10 px-3 py-1 text-xs font-semibold text-brand-accent"
                          >
                            {med}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-500">No medications prescribed.</span>
                      )}
                    </dd>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                      <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Follow-up date
                      </dt>
                      <dd className="mt-2 text-slate-700">
                        {record.followUpDate
                          ? new Date(record.followUpDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'No follow-up scheduled'}
                      </dd>
                    </div>
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                      <dt className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Visit cost
                      </dt>
                      <dd className="mt-2 text-lg font-semibold text-slate-800">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        }).format(record.cost)}
                      </dd>
                    </div>
                  </div>
                </dl>

                {record.notes && (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                    <span className="font-semibold text-slate-500">Notes:</span> {record.notes}
                  </div>
                )}

                <footer className="flex items-center justify-end">
                  <button
                    className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100"
                    onClick={() => handleDelete(record.id)}
                  >
                    🗑 Remove record
                  </button>
                </footer>
              </article>
            ))}
          </div>
        )}
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            <button
              className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-500 transition hover:bg-slate-100"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >
              ✕
            </button>

            <header>
              <h2 className="text-xl font-semibold text-slate-800">Add medical record</h2>
              <p className="mt-2 text-sm text-slate-500">
                Document diagnoses, treatments, and medication plans for this visit.
              </p>
            </header>

            <form className="mt-6 space-y-4 text-sm" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Patient name *
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.patientName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, patientName: e.target.value }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Owner name *
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.ownerName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ownerName: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Visit date *
                  <input
                    type="date"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Veterinarian *
                  <select
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.veterinarian}
                    onChange={(e) => setFormData((prev) => ({ ...prev, veterinarian: e.target.value }))}
                    required
                  >
                    <option value="">Select a veterinarian</option>
                    {veterinarianOptions.map((vet) => (
                      <option key={vet} value={vet}>
                        {vet}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Visit type *
                  <select
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.type}
                    onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
                    required
                  >
                    {recordTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Follow-up date
                  <input
                    type="date"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.followUpDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, followUpDate: e.target.value }))}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 font-semibold text-slate-600">
                Diagnosis *
                <textarea
                  rows={2}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.diagnosis}
                  onChange={(e) => setFormData((prev) => ({ ...prev, diagnosis: e.target.value }))}
                  placeholder="Describe symptoms, results, and findings."
                  required
                />
              </label>

              <label className="flex flex-col gap-2 font-semibold text-slate-600">
                Treatment plan *
                <textarea
                  rows={2}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.treatment}
                  onChange={(e) => setFormData((prev) => ({ ...prev, treatment: e.target.value }))}
                  placeholder="Outline procedures performed and recommendations."
                  required
                />
              </label>

              <label className="flex flex-col gap-2 font-semibold text-slate-600">
                Medications (comma-separated)
                <input
                  type="text"
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.medications}
                  onChange={(e) => setFormData((prev) => ({ ...prev, medications: e.target.value }))}
                  placeholder="e.g., Medication 1, Medication 2"
                />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Cost (USD)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.cost}
                    onChange={(e) => setFormData((prev) => ({ ...prev, cost: e.target.value }))}
                    placeholder="0.00"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 font-semibold text-slate-600">
                Additional notes
                <textarea
                  rows={3}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any follow-up reminders or recovery notes."
                />
              </label>

              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                >
                  Save record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalRecords;
